import { z } from "zod";
import { agentTranscriptItemSchema } from "./messages.js";
export const MAX_CONTINUITY_CHUNK_BYTES = 4 * 1024 * 1024;
export const MAX_CONTINUITY_MANIFEST_BYTES = 64 * 1024 * 1024;
export const MAX_CONTINUITY_HISTORY_BYTES = 1024 * 1024;
export const MAX_CONTINUITY_HISTORY_ITEMS = 100;
export const continuityDigestSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);
export const continuitySequenceSchema = z.number().int().min(-1);
export const continuityCapabilitySchema = z.object({ schemaVersion: z.literal(1) }).strict();
export const continuityIdentitySchema = z.object({
    sessionId: z.uuid(),
    generation: z.uuid(),
    workspaceGeneration: z.uuid(),
}).strict();
export const workspaceArchiveIdentitySchema = z.object({ workspaceGeneration: z.uuid() }).strict();
export const workspaceCheckpointRequestSchema = z.object({ schemaVersion: z.literal(1), eventId: z.uuid() }).strict();
export const agentAppContinuityRetireRequestSchema = z.object({
    schemaVersion: z.literal(1),
    appId: z.uuid(),
    operationId: z.uuid(),
    state: z.literal("retiring"),
}).strict();
export const agentAppContinuityRetireResultSchema = z.object({
    schemaVersion: z.literal(1),
    appId: z.uuid(),
    operationId: z.uuid(),
    state: z.literal("retired"),
}).strict();
export const continuityObjectKeySchema = z.string().min(1).max(1_024).refine((value) => !/[\\\x00-\x1f\x7f]/.test(value) &&
    value.split("/").every((part) => part !== "" && part !== "." && part !== ".."), "A continuity object key must be a bounded relative key");
export const continuityCheckpointReferenceSchema = z.object({
    id: z.uuid(),
    version: z.number().int().positive(),
    digest: continuityDigestSchema,
    objectKey: continuityObjectKeySchema,
}).strict();
export const continuityWorkspaceCheckpointSchema = z.object({
    checkpoint: continuityCheckpointReferenceSchema,
    roots: z.array(z.enum(["workspace", "mutations"])).min(1).max(2)
        .refine(roots => roots.includes("workspace") && new Set(roots).size === roots.length, "Workspace recovery requires unique source roots and cannot contain native state"),
}).strict();
export const workspaceStorageSchema = z.object({
    schemaVersion: z.literal(1),
    generation: z.uuid(),
    version: z.number().int().nonnegative(),
    initializedAt: z.iso.datetime().nullable(),
    unsaved: z.boolean().optional(),
    checkpoint: continuityWorkspaceCheckpointSchema.optional(),
}).strict().refine(value => !value.checkpoint || value.checkpoint.checkpoint.version === value.version, "Workspace recovery must match the exact current source version");
export const continuitySessionSchema = z.object({
    initializedAt: z.iso.datetime().nullable(),
    checkpointWorkspaceVersion: z.number().int().positive().nullable().optional(),
    checkpointRoots: z.array(z.enum(["workspace", "native", "mutations"])).min(1).max(3).optional(),
    workspaceCheckpoint: continuityWorkspaceCheckpointSchema.optional(),
}).strict();
export const runtimeContinuityBindingSchema = z.object({
    schemaVersion: z.literal(1),
    ...continuityIdentitySchema.shape,
    mode: z.enum(["create", "resume", "bootstrap"]),
    workspaceState: z.enum(["new", "ready", "adopt"]),
    historyThroughSequence: continuitySequenceSchema,
    checkpoint: continuityCheckpointReferenceSchema.nullable(),
    adoption: z.object({
        workspaceAppId: z.uuid(),
        nativeRunId: z.uuid().optional(),
        nativeRuntimeAttempt: z.number().int().positive().optional(),
    }).strict().optional(),
    /**
     * The previous writer stopped mid-turn, so this root continues under a
     * fresh native generation that bootstraps from admitted history while the
     * shared workspace keeps whatever that turn left on disk.
     */
    recovery: z.object({ reason: z.literal("interrupted_turn") }).strict().optional(),
}).strict().superRefine((value, context) => {
    if (Boolean(value.adoption) !== (value.workspaceState === "adopt")) {
        context.addIssue({ code: "custom", path: ["adoption"], message: "Adoption inventory requires an adopt workspace" });
    }
    if (value.adoption && Boolean(value.adoption.nativeRunId) !== Boolean(value.adoption.nativeRuntimeAttempt)) {
        context.addIssue({ code: "custom", path: ["adoption"], message: "Native adoption requires an exact run and attempt together" });
    }
    if (value.recovery && value.mode !== "bootstrap") {
        context.addIssue({ code: "custom", path: ["recovery"], message: "Interrupted-turn recovery bootstraps a fresh native generation" });
    }
});
export const continuityHistoryRequestSchema = z.object({
    schemaVersion: z.literal(1),
    afterSequence: continuitySequenceSchema,
    throughSequence: continuitySequenceSchema,
    limit: z.number().int().min(1).max(MAX_CONTINUITY_HISTORY_ITEMS),
}).strict().refine((value) => value.afterSequence <= value.throughSequence, "History bounds must be ordered");
export const continuityHistoryResultSchema = z.object({
    schemaVersion: z.literal(1),
    items: z.array(agentTranscriptItemSchema).max(MAX_CONTINUITY_HISTORY_ITEMS),
    nextAfterSequence: z.number().int().nonnegative().nullable(),
    throughSequence: continuitySequenceSchema,
}).strict().superRefine((value, context) => {
    const ids = new Set();
    let previousSequence = -1;
    for (const item of value.items) {
        if (ids.has(item.id) || item.sequence <= previousSequence || item.sequence > value.throughSequence) {
            context.addIssue({ code: "custom", message: "History must contain unique, ordered, admitted items" });
            break;
        }
        ids.add(item.id);
        previousSequence = item.sequence;
    }
    if (value.nextAfterSequence !== null &&
        (value.items.length === 0 || value.nextAfterSequence !== previousSequence || previousSequence >= value.throughSequence)) {
        context.addIssue({ code: "custom", message: "A history cursor must advance to the last returned item" });
    }
    if (new TextEncoder().encode(JSON.stringify(value)).byteLength > MAX_CONTINUITY_HISTORY_BYTES) {
        context.addIssue({ code: "custom", message: "History response exceeds its byte limit" });
    }
});
export const continuityHistoryItemResultSchema = z.object({
    schemaVersion: z.literal(1),
    item: agentTranscriptItemSchema,
}).strict();
export const continuityDeliveryStateSchema = z.enum(["pending", "unknown", "accepted", "completed", "cancelled"]);
export const continuityDeliveryUpdateSchema = z.object({
    schemaVersion: z.literal(1),
    commandId: z.uuid(),
    messageId: z.uuid(),
    payloadDigest: continuityDigestSchema,
    state: continuityDeliveryStateSchema,
    nativeThreadId: z.string().min(1).max(256).nullable(),
    nativeTurnId: z.string().min(1).max(256).nullable(),
}).strict().refine((value) => !["accepted", "completed"].includes(value.state) ||
    (value.nativeThreadId !== null && value.nativeTurnId !== null), "Accepted delivery requires native acceptance coordinates");
export const continuityCheckpointRequestSchema = z.object({
    schemaVersion: z.literal(1),
    reason: z.enum(["turn", "pause", "activation", "compaction", "idle"]),
    expectedVersion: z.number().int().nonnegative(),
    historyThroughSequence: continuitySequenceSchema,
    deliveryThroughSequence: continuitySequenceSchema,
    compactionVersion: z.number().int().nonnegative(),
}).strict();
export const continuityCheckpointResultSchema = z.object({
    schemaVersion: z.literal(1),
    checkpoint: continuityCheckpointReferenceSchema,
}).strict();
export const continuityObjectKindSchema = z.enum(["chunk", "manifest"]);
export const continuityObjectReceiptSchema = z.object({
    schemaVersion: z.literal(1),
    kind: continuityObjectKindSchema,
    digest: continuityDigestSchema,
    objectKey: continuityObjectKeySchema,
    byteLength: z.number().int().min(1).max(MAX_CONTINUITY_MANIFEST_BYTES),
}).strict().refine((value) => value.kind !== "chunk" || value.byteLength <= MAX_CONTINUITY_CHUNK_BYTES, "Chunk exceeds its transfer limit");
export const continuityStatusSchema = z.object({
    schemaVersion: z.literal(1),
    ...continuityIdentitySchema.shape,
    state: z.enum(["new", "ready", "checkpointing", "restoring", "compacting", "recovery_required", "retiring"]),
    checkpoint: continuityCheckpointReferenceSchema.nullable(),
    historyThroughSequence: continuitySequenceSchema,
    deliveryThroughSequence: continuitySequenceSchema,
    compactionVersion: z.number().int().nonnegative(),
}).strict();
export const continuityCompactionReceiptSchema = z.object({
    schemaVersion: z.literal(1),
    id: z.uuid(),
    version: z.number().int().positive(),
    state: z.enum(["started", "completed", "interrupted"]),
    trigger: z.enum(["native", "idle", "bootstrap"]),
    fromSequence: continuitySequenceSchema,
    throughSequence: continuitySequenceSchema,
    sourceDigest: continuityDigestSchema,
    checkpoint: continuityCheckpointReferenceSchema.nullable(),
}).strict().refine((value) => value.fromSequence <= value.throughSequence, "Compaction source bounds must be ordered");
export const continuityFailureSchema = z.object({
    schemaVersion: z.literal(1),
    code: z.enum(["recovery_required", "stale_writer", "missing_object", "corrupt_archive", "storage_unavailable", "delivery_unknown"]),
    retryable: z.boolean(),
}).strict();
export const agentContinuityViewSchema = z.object({
    schemaVersion: z.literal(1),
    state: z.enum(["new", "ready", "checkpointing", "restoring", "compacting", "recovery_required", "retiring"]),
    checkpointVersion: z.number().int().nonnegative(),
    savedAt: z.iso.datetime().nullable(),
    failureCode: continuityFailureSchema.shape.code.nullable(),
    workspaceChange: z.object({ version: z.number().int().positive(), savedAt: z.iso.datetime() }).strict().optional(),
}).strict();
export const CONTINUITY_HELPER_EXECUTABLE = "/app/infra/agent-runtime/dist/continuity-helper-main.js";
export const CONTINUITY_HELPER_REQUEST_FILE = "/run/opencloud/continuity-helper/request.json";
export const CONTINUITY_HELPER_TOKEN_FILE = "/run/opencloud/callback/token";
export const continuityHelperRequestSchema = z.object({
    schemaVersion: z.literal(1),
    operation: z.enum(["snapshot", "restore"]),
    userId: z.uuid(),
    appId: z.uuid(),
    runId: z.uuid(),
    runtimeAttempt: z.number().int().positive(),
    continuity: runtimeContinuityBindingSchema.optional(),
    workspace: workspaceStorageSchema.optional(),
    workspaceCheckpoint: continuityWorkspaceCheckpointSchema.optional(),
    restoreRoots: z.array(z.enum(["workspace", "native", "mutations"])).min(1).max(3)
        .refine(roots => new Set(roots).size === roots.length, "Restore roots must be unique").optional(),
    callback: z.object({
        url: z.url().max(2_048),
        tokenFile: z.literal(CONTINUITY_HELPER_TOKEN_FILE),
    }).strict(),
}).strict().refine(value => Boolean(value.continuity) !== Boolean(value.workspace), "Helper requires exactly one storage binding")
    .refine(value => !value.workspace || value.operation === "snapshot" ||
    value.workspaceCheckpoint && value.restoreRoots?.every(root => root !== "native") &&
        value.workspaceCheckpoint.checkpoint.version === value.workspace.version && !value.workspace.unsaved, "Workspace-only restoration requires an exact source checkpoint with no unsaved tail")
    .refine((value) => value.operation !== "restore" || value.continuity?.checkpoint != null || value.workspaceCheckpoint !== undefined, "Restore requires an immutable checkpoint").refine(value => !value.restoreRoots || value.operation === "restore", "Root selection is only available for verified restoration").refine(value => !value.workspaceCheckpoint ||
    value.operation === "restore" && value.restoreRoots?.every(root => root !== "native" && value.workspaceCheckpoint.roots.includes(root)), "Workspace recovery must select only verified source roots");
export function continuityDeliveryPayload(command) {
    if (!("input" in command) || !command.input) {
        throw new Error("Continuity delivery requires command input");
    }
    return JSON.stringify(canonicalValue({
        messageId: command.input.messageId,
        content: command.input.content,
        attachments: command.input.attachments ?? [],
        actor: command.input.actor ?? null,
        requestingActor: command.actor ?? null,
        questionId: command.type === "user.answer" ? command.questionId : null,
    }));
}
function canonicalValue(value) {
    if (Array.isArray(value))
        return value.map(canonicalValue);
    if (value !== null && typeof value === "object") {
        return Object.fromEntries(Object.entries(value).sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0)
            .map(([key, child]) => [key, canonicalValue(child)]));
    }
    return value;
}
//# sourceMappingURL=continuity.js.map